import {ReactElement, useCallback, useMemo} from 'react';
import VectorIcon from './VectorIcon';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import {CodeResponse, useGoogleLogin} from '@react-oauth/google';
import {useAuthService} from '../contexts/AuthServiceProvider.client';
import {config} from '../config';

interface SocialAuthBlockProps {
  action?: 'signin' | 'signup';
}

interface FacebookLoginResponse {
  id: string;
  name: string;
  email: string;
  picture: {
    data: {
      height: number;
      is_silhouette: boolean;
      url: string;
      width: number;
    };
  };
  accessToken: string;
  userID: string;
  expiresIn: number;
  signedRequest: string;
  graphDomain: string;
  data_access_expiration_time: number;
}

export default function SocialAuthBlock({
  action = 'signin',
}: SocialAuthBlockProps): ReactElement {
  const actionLabel = useMemo(
    () => (action === 'signin' ? 'sign in' : 'sign up'),
    [action],
  );

  const capitalize = useCallback(
    (word: string) => word.charAt(0).toUpperCase() + word.slice(1),
    [],
  );

  const {handleSocialAuth} = useAuthService();

  const responseFacebook = useCallback(
    (response: FacebookLoginResponse) => {
      handleSocialAuth({
        auth_provider: 'facebook',
        token: response.accessToken,
        user_id: response.userID,
        name: response.name,
        email: response.email,
        profile_picture_url: response.picture?.data?.url,
      });
    },
    [handleSocialAuth],
  );

  const handleGoogleLogin = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: (response: CodeResponse) => {
      if ({}.hasOwnProperty.call(response, 'error')) {
        return;
      }

      handleSocialAuth({
        auth_provider: 'google',
        token: response.code,
      });
    },
  });

  return (
    <>
      <div className="signup__social">
        {config && config.googleOauth2ClientId && (
          <>
            <button
              onClick={() => handleGoogleLogin()}
              className="form__element material-bubble signup__social-google"
            >
              <VectorIcon iconName="google-i" />
              <span>{capitalize(actionLabel)} with Google</span>
            </button>
          </>
        )}
        {config && config.facebookOauth2ClientId && (
          <FacebookLogin
            appId={config.facebookOauth2ClientId}
            fields="id,name,email,picture"
            callback={responseFacebook}
            render={(renderProps) => (
              <button
                onClick={renderProps.onClick}
                className="form__element material-bubble signup__social-facebook"
              >
                <VectorIcon iconName="facebook-i" />
                <span>{capitalize(actionLabel)} with Facebook</span>
              </button>
            )}
          />
        )}
        {/* <button className="form__element material-bubble signup__social-apple">
          <VectorIcon iconName="apple" />
          <span>{capitalize(actionLabel)} with Apple</span>
        </button> */}
      </div>
      <div className="signup__or">
        <span>Or {actionLabel} with email</span>
      </div>
    </>
  );
}
